import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { trackEvent, ANALYTICS_EVENTS } from "@/lib/analytics";
import { sanitizeRedirect } from "@/lib/safeRedirect";
import { recordShareEvent, storedShareCampaign, storedShareSlug } from "@/lib/share";

export default function Auth() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const initialMode = searchParams.get("mode") === "signup" ? "signup" : "signin";
  const redirectParam = searchParams.get("redirect");
  const [mode, setMode] = useState<"signin" | "signup">(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  // Only allow same-origin paths / allowlisted hash anchors. See safeRedirect.ts.
  // Hash-only targets (e.g. "#pricing") get rooted at "/" so they land on the
  // landing page section rather than on "/auth#pricing".
  const safeRedirectRaw = sanitizeRedirect(redirectParam);
  const safeRedirect = safeRedirectRaw?.startsWith("#")
    ? `/${safeRedirectRaw}`
    : safeRedirectRaw;
  const postAuthTarget = safeRedirect || "/dashboard";



  useEffect(() => {
    if (user) navigate(postAuthTarget, { replace: true });
  }, [user, navigate, postAuthTarget]);


  const friendlyAuthError = (raw: string): string => {
    const m = raw.toLowerCase();
    if (m.includes("invalid login")) return "Email or password is incorrect.";
    if (m.includes("email not confirmed")) return "Please confirm your email before signing in.";
    if (m.includes("user already registered") || m.includes("already exists"))
      return "An account with this email already exists. Try signing in instead.";
    if (m.includes("password should be") || m.includes("password is too"))
      return "Password must be at least 8 characters.";
    if (m.includes("rate limit") || m.includes("too many"))
      return "Too many attempts. Please wait a minute and try again.";
    if (m.includes("network") || m.includes("fetch"))
      return "Network error — check your connection and try again.";
    return raw || "Something went wrong. Please try again.";
  };

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  const validate = () => {
    const trimmed = email.trim();
    const nextEmailError = !trimmed
      ? "Enter your email address."
      : trimmed.length > 254
        ? "Email is too long."
        : !EMAIL_RE.test(trimmed)
          ? "That doesn't look like a valid email."
          : null;
    const nextPasswordError = !password
      ? "Enter your password."
      : password.length < 8
        ? "Password must be at least 8 characters."
        : null;
    setEmailError(nextEmailError);
    setPasswordError(nextPasswordError);
    return !nextEmailError && !nextPasswordError;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      if (mode === "signup") {
        const { error } = await supabase.auth.signUp({
          email: email.trim(),
          password,
          options: { emailRedirectTo: `${window.location.origin}${safeRedirect || "/dashboard"}` },
        });
        if (error) throw error;
        trackEvent(ANALYTICS_EVENTS.SIGN_UP, { method: "email" });
        // Credit the share link + campaign that brought this signup in.
        const shareSlug = storedShareSlug();
        if (shareSlug) void recordShareEvent(shareSlug, "signup", storedShareCampaign());
        toast.success("Welcome to Trends Discovery");
        navigate(safeRedirect || "/onboarding", { replace: true });
        return;
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email: email.trim(),
          password,
        });
        if (error) throw error;
        trackEvent(ANALYTICS_EVENTS.SIGN_IN, { method: "email" });
        toast.success("Signed in");
        navigate(postAuthTarget, { replace: true });
      }

    } catch (err: any) {
      trackEvent(ANALYTICS_EVENTS.SIGN_IN_FAILED, { mode });
      toast.error(friendlyAuthError(err?.message ?? ""));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{mode === "signin" ? "Sign in" : "Create account"} — Trends Discovery</title>
        <meta name="description" content={mode === "signin" ? "Sign in to your Trends Discovery account to access your live trend feed, alerts, and launch blueprints." : "Create a Trends Discovery account to unlock live trend signals, alerts, and complete launch blueprints with your lifetime license."} />
        <link rel="canonical" href="https://trendsdiscovery.com/auth" />
        <meta property="og:title" content={`${mode === "signin" ? "Sign in" : "Create account"} — Trends Discovery`} />
        <meta property="og:url" content="https://trendsdiscovery.com/auth" />
      </Helmet>
    <div className="min-h-screen grid place-items-center px-4 bg-hero-radial">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 shadow-elevated">
        <Link to="/" className="font-mono text-xs text-primary">← back</Link>
        <h1 className="font-display text-3xl font-bold mt-4">
          {mode === "signin" ? "Sign in" : "Create account"}
        </h1>
        <p className="text-sm text-muted-foreground mt-2">
          Access your Trends Discovery dashboard.
        </p>
        <form onSubmit={handleSubmit} noValidate className="mt-6 space-y-4">
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailError) setEmailError(null);
              }}
              autoComplete="email"
              aria-invalid={!!emailError}
              aria-describedby={emailError ? "email-error" : undefined}
            />
            {emailError && (
              <p
                id="email-error"
                role="status"
                data-testid="auth-email-error"
                className="text-xs text-destructive mt-1.5"
              >
                {emailError}
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (passwordError) setPasswordError(null);
              }}
              autoComplete={mode === "signup" ? "new-password" : "current-password"}
              aria-invalid={!!passwordError}
              aria-describedby={
                passwordError ? "password-error" : mode === "signup" ? "password-hint" : undefined
              }
            />
            {passwordError ? (
              <p
                id="password-error"
                role="status"
                data-testid="auth-password-error"
                className="text-xs text-destructive mt-1.5"
              >
                {passwordError}
              </p>
            ) : (
              mode === "signup" && (
                <p id="password-hint" className="text-xs text-muted-foreground mt-1.5">
                  At least 8 characters.
                </p>
              )
            )}
          </div>
          <Button type="submit" variant="hero" size="lg" className="w-full" disabled={loading}>
            {loading ? "…" : mode === "signin" ? "Sign in" : "Create account"}
          </Button>
        </form>
        <div className="mt-4 flex items-center justify-between text-sm">
          <button
            onClick={() => setMode(mode === "signin" ? "signup" : "signin")}
            className="text-muted-foreground hover:text-foreground"
          >
            {mode === "signin" ? "No account? Create one" : "Have an account? Sign in"}
          </button>
          {mode === "signin" && (
            <Link to="/forgot-password" className="text-muted-foreground hover:text-foreground">
              Forgot password?
            </Link>
          )}
        </div>
      </div>
    </div>
    </>
  );
}
